import type { CrashReportSummary, Message, Thread } from '@central/api';
import {
  charCount,
  codeBlock,
  color,
  font,
  formatAgo,
  formatDateTime,
  internalNote,
} from '@central/ui';
import { Alert, Button, Textarea } from '@mantine/core';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { CannedReplyPicker } from '../components/CannedReplies';
import { QueryState } from '../components/parts';
import { errorMessage } from '../lib/errors';
import { formatBytes } from '../lib/format';
import { muted } from '../lib/labels';
import { useIsReseller } from '../lib/me';
import { CrashDiagnostics } from './CrashDiagnostics';
import { useThreadsApi } from '../lib/threads';

const stackBlock: CSSProperties = {
  margin: 0,
  background: codeBlock.bg,
  color: codeBlock.fg,
  borderRadius: 8,
  padding: '14px 16px',
  fontFamily: font.mono,
  fontSize: 12,
  lineHeight: '19px',
  overflowX: 'auto',
};

const attachmentRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '10px 12px',
  border: `1px solid ${color.border}`,
  borderRadius: 8,
  fontSize: 13,
};

const reportLabel = (r: CrashReportSummary) =>
  [formatDateTime(r.at), r.instanceKey, r.version].filter(Boolean).join(' · ');

/** A crash thread's reports, newest first; one at a time shows its stack and bundle. */
export function CrashReports({ threadId, customerId }: { threadId: string; customerId: string }) {
  const api = useApi();
  const list = useInfiniteQuery({
    queryKey: keys.threadCrashReports(threadId),
    queryFn: ({ pageParam }) => api.threads.crashReports(threadId, { cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const [picked, setPicked] = useState<string>();
  const reports = list.data?.pages.flatMap((p) => p.items) ?? [];
  const selected = picked ?? reports[0]?.id;
  return (
    <section
      aria-labelledby={`crash-reports-${threadId}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <h3 id={`crash-reports-${threadId}`} style={{ margin: 0, fontSize: 14 }}>
        Crash reports
      </h3>
      <QueryState isPending={list.isPending} error={list.error}>
        {() =>
          reports.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, ...muted }}>No reports on this thread.</p>
          ) : (
            <>
              {reports.length > 1 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {reports.map((r) => (
                    <Button
                      key={r.id}
                      size="xs"
                      variant={r.id === selected ? 'filled' : 'default'}
                      color="dark"
                      aria-pressed={r.id === selected}
                      onClick={() => setPicked(r.id)}
                    >
                      {reportLabel(r)}
                    </Button>
                  ))}
                  {list.hasNextPage && (
                    <Button
                      size="xs"
                      variant="subtle"
                      loading={list.isFetchingNextPage}
                      onClick={() => void list.fetchNextPage()}
                    >
                      Older reports
                    </Button>
                  )}
                </div>
              )}
              {selected && (
                <CrashReportDetail key={selected} id={selected} customerId={customerId} />
              )}
            </>
          )
        }
      </QueryState>
    </section>
  );
}

function CrashReportDetail({ id, customerId }: { id: string; customerId: string }) {
  const api = useApi();
  const q = useQuery({ queryKey: keys.crashReport(id), queryFn: () => api.crashReports.get(id) });
  return (
    <QueryState isPending={q.isPending} error={q.error}>
      {() => {
        const r = q.data!;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 12, ...muted }}>
              {[r.service, r.instanceKey, r.version, r.tenantId, formatDateTime(r.at)]
                .filter(Boolean)
                .join(' · ')}
            </span>
            {r.stack ? (
              <pre style={stackBlock}>{r.stack}</pre>
            ) : (
              <p style={{ margin: 0, fontSize: 13, ...muted }}>No stack trace was sent.</p>
            )}
            <CrashDiagnostics r={r} customerId={customerId} />
          </div>
        );
      }}
    </QueryState>
  );
}

const bubble: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 14,
  lineHeight: '21px',
};

const eventVerb = {
  resolved: 'Resolved',
  reopened: 'Reopened',
  escalated: 'Escalated to Rethink',
  returned: 'Returned to the reseller',
} as const;

export function MessageItem({ m, customerName }: { m: Message; customerName: string }) {
  const when = formatAgo(m.at);
  if (m.event === 'assigned') {
    const by = m.author.name ?? 'Staff';
    return (
      <li
        data-event={m.event}
        data-internal
        style={{ fontSize: 12, textAlign: 'center', ...muted }}
      >
        <b style={{ color: color.text }}>
          {m.assignee
            ? `Assigned to ${m.assignee.name ?? 'staff'} by ${by}`
            : `Unassigned by ${by}`}
        </b>{' '}
        · {when} · not visible to customer
      </li>
    );
  }
  if (m.event) {
    const who =
      m.author.kind === 'customer'
        ? `${m.author.name ?? 'Customer'} (${customerName})`
        : (m.author.name ?? 'Staff');
    return (
      <li data-event={m.event} style={{ fontSize: 12, textAlign: 'center', ...muted }}>
        <b style={{ color: color.text }}>
          {eventVerb[m.event]} by {who}
        </b>{' '}
        · {when}
        {m.body && (
          <div
            style={{
              marginTop: 6,
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              fontSize: 14,
              color: color.text,
              background: color.page,
              borderRadius: 8,
              padding: '10px 12px',
            }}
          >
            {m.body}
          </div>
        )}
      </li>
    );
  }
  if (m.author.kind === 'system') {
    return (
      <li style={bubble}>
        <span style={{ fontSize: 12, ...muted }}>
          <b style={{ color: color.text }}>Crash report</b> · {when}
        </span>
        <pre style={stackBlock}>{m.body}</pre>
        <Attachments m={m} />
      </li>
    );
  }
  const header = m.internal ? (
    <>
      <b style={{ color: color.text }}>Internal note</b> · {m.author.name ?? 'Staff'} · not visible
      to customer
    </>
  ) : m.author.kind === 'customer' ? (
    <>
      <b style={{ color: color.text }}>{m.author.name ?? 'Customer'}</b> · {customerName} · {when}
    </>
  ) : (
    <>
      <b style={{ color: color.text }}>{m.author.name ?? 'Staff'}</b> · {when}
    </>
  );
  return (
    <li
      data-internal={m.internal || undefined}
      style={
        m.internal
          ? {
              ...bubble,
              background: internalNote.bg,
              border: `1px solid ${internalNote.border}`,
              borderRadius: 8,
              padding: '10px 12px',
            }
          : bubble
      }
    >
      <span style={{ fontSize: 12, ...muted }}>{header}</span>
      <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.body}</div>
      <Attachments m={m} />
    </li>
  );
}

function Attachments({ m }: { m: Message }) {
  if (m.attachments.length === 0) return null;
  return (
    <ul
      style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {m.attachments.map((a) => (
        <li key={a.key} style={attachmentRow}>
          {a.url ? (
            <a href={a.url} rel="noreferrer">
              {a.name}
            </a>
          ) : (
            <span>{a.name}</span>
          )}
          <span style={{ fontFamily: font.mono, ...muted }}>{formatBytes(a.bytes)}</span>
        </li>
      ))}
    </ul>
  );
}

const MAX_BODY = 20_000;

/**
 * One draft posted one way: a reply (the customer sees it and is emailed) or
 * an internal note. Each form owns its text, its error and its post.
 */
function useDraft(t: Thread, kind: 'reply' | 'note') {
  const threads = useThreadsApi();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [problem, setProblem] = useState<string>();
  const post = useMutation({
    mutationFn: (b: string) =>
      kind === 'reply' ? threads.reply(t.id, { body: b }) : threads.addNote(t.id, { body: b }),
    onSuccess: async () => {
      setBody('');
      await qc.invalidateQueries({ queryKey: keys.threads });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const b = body.trim();
    const p = !b
      ? 'Write something first.'
      : charCount(b) > MAX_BODY
        ? 'Keep it under 20,000 characters.'
        : undefined;
    setProblem(p);
    if (!p && !post.isPending) post.mutate(b);
  };
  const reset = () => {
    setBody('');
    setProblem(undefined);
    post.reset();
  };
  return { body, setBody, problem, post, submit, reset };
}

/** The reply to the customer, then the internal note: two forms that never share text. */
export function Composer({ t }: { t: Thread }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <ReplyForm t={t} />
      <NoteForm t={t} />
    </div>
  );
}

const formHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  padding: '12px 16px',
};

function EnvelopeIcon() {
  return (
    <svg
      aria-hidden
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color.link}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

function LockIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={internalNote.icon}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function ReplyForm({ t }: { t: Thread }) {
  const d = useDraft(t, 'reply');
  const box = useRef<HTMLTextAreaElement>(null);
  const heading = `reply-${t.id}`;
  const busy = d.post.isPending;
  return (
    <form
      aria-labelledby={heading}
      onSubmit={d.submit}
      noValidate
      style={{
        background: color.surface,
        border: `1px solid ${color.borderStrong}`,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ ...formHead, borderBottom: `1px solid ${color.border}` }}>
        <EnvelopeIcon />
        <h2 id={heading} style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          Reply to customer
        </h2>
        <span style={{ fontSize: 13, ...muted }}>{t.customer.name} sees this and is emailed</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px' }}>
        <Textarea
          ref={box}
          aria-label={`Reply to ${t.customer.name}`}
          placeholder={`Write to ${t.customer.name}`}
          autosize
          minRows={4}
          value={d.body}
          onChange={(e) => d.setBody(e.currentTarget.value)}
          disabled={busy}
        />
        <CannedReplyPicker
          t={t}
          value={d.body}
          onChange={d.setBody}
          textarea={box}
          disabled={busy}
        />
        {(d.problem || d.post.error) && (
          <Alert color="red">{d.problem ?? errorMessage(d.post.error)}</Alert>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" size="md" color={color.link} loading={busy} disabled={busy}>
            Send to customer
          </Button>
        </div>
      </div>
    </form>
  );
}

/** An internal note, collapsed to a button until asked for; Cancel drops the draft. */
function NoteForm({ t }: { t: Thread }) {
  const reseller = useIsReseller();
  const d = useDraft(t, 'note');
  const [open, setOpen] = useState(false);
  const heading = `note-${t.id}`;
  const busy = d.post.isPending;
  const dashed = `1px dashed ${internalNote.dashed}`;
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 44,
          padding: '0 14px',
          borderRadius: 8,
          border: dashed,
          background: internalNote.bg,
          color: internalNote.fg,
          font: 'inherit',
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        <LockIcon size={16} />
        Add internal note
      </button>
    );
  }
  return (
    <form
      aria-labelledby={heading}
      onSubmit={d.submit}
      noValidate
      style={{
        background: internalNote.bg,
        border: dashed,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        color: internalNote.fg,
      }}
    >
      <div style={{ ...formHead, borderBottom: `1px dashed ${internalNote.rule}` }}>
        <LockIcon />
        <h2 id={heading} style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          Internal note
        </h2>
        <span style={{ fontSize: 13 }}>
          {reseller
            ? 'Only your team and Rethink staff see this. The customer is not emailed.'
            : 'Only Rethink staff see this. The customer is not emailed.'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px' }}>
        <Textarea
          aria-label="Internal note"
          placeholder="Note for the team"
          autosize
          minRows={3}
          autoFocus
          value={d.body}
          onChange={(e) => d.setBody(e.currentTarget.value)}
          disabled={busy}
        />
        {(d.problem || d.post.error) && (
          <Alert color="red">{d.problem ?? errorMessage(d.post.error)}</Alert>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button
            type="button"
            variant="subtle"
            size="md"
            color={internalNote.fg}
            disabled={busy}
            onClick={() => {
              d.reset();
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="md"
            loading={busy}
            disabled={busy}
            styles={{
              root: {
                background: internalNote.button,
                color: internalNote.fg,
                border: `1px solid ${internalNote.dashed}`,
              },
            }}
          >
            Add internal note
          </Button>
        </div>
      </div>
    </form>
  );
}
